import { DatapointStatus, lazyLogSigsDatapoint, PerformanceDatapoint } from 'owa-analytics';
import {
    getSaveToCloudEstimatedCompleteTimeInMS,
    saveToCloudPostProcess,
    saveToCloudPreProcess,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import { lazyGetAttachment } from 'owa-attachment-model-store';
import setAttachmentOriginalUrl from 'owa-attachment-model-store/lib/actions/setAttachmentOriginalUrl';
import getAttachmentFileType from 'owa-attachment-model-store/lib/utils/getAttachmentFileType';
import { saveAttachmentsToCloud, SaveToCloudStatus } from 'owa-attachment-savetocloud';
import { isFeatureEnabled } from 'owa-feature-flags';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import SaveToCloudNotificationPayload from 'owa-service/lib/contract/SaveToCloudNotificationPayload';
import { sxsStore } from 'owa-sxs-store';
import { orchestrator } from 'satcheljs';
import { setSaveToCloudStatus } from '../actions/internalActions';
import { saveToCloud } from '../actions/publicLazyActions';
import PreviewPaneSelectionSource from '../store/schema/PreviewPaneSelectionSource';
import tryGetAttachmentFullViewStateByIdInReadingPane from '../utils/tryGetAttachmentFullViewStateByIdInReadingPane';

orchestrator(saveToCloud, async actionMessage => {
    const { entryPoint } = actionMessage;
    const datapoint = new PerformanceDatapoint('AttachmentSaveToCloud');

    // We are creating a local copy of this here because setSaveToCloudStatus and setAttachmentOriginalUrl depend on the current attachmentId, and
    // if you change which attachment you are viewing while save is in progress attachmentId will have changed
    // by the time we call the function.
    const attachmentId = sxsStore.extendedViewState.attachmentId;
    const getAttachment = await lazyGetAttachment.import();
    const attachmentModel = getAttachment(attachmentId);
    const attachmentSize = attachmentModel ? attachmentModel.model.Size : 0;
    const datapointCustomData = {
        entryPoint: entryPoint,
        result: null,
        actionSource: PreviewPaneSelectionSource[sxsStore.extendedViewState.selectionSource],
    };
    const attachmentFullViewState = tryGetAttachmentFullViewStateByIdInReadingPane(attachmentId);

    datapoint.addCosmosOnlyData(
        JSON.stringify({
            size: attachmentSize,
            fileType: getAttachmentFileType(attachmentModel),
        })
    );

    if (attachmentFullViewState) {
        startFakeProgressIndicator(
            getSaveToCloudEstimatedCompleteTimeInMS(attachmentFullViewState.attachmentId),
            0.1,
            (completePercent: number) => {
                setActionCompletePercent(attachmentFullViewState, completePercent);
            },
            attachmentFullViewState.attachmentId
        );

        saveToCloudPreProcess(attachmentFullViewState);
    }

    const notificationHandler = (payload: SaveToCloudNotificationPayload): boolean => {
        datapointCustomData.result = payload.ResultCode;
        datapoint.addCustomData(datapointCustomData);

        const succeeded = payload.ResultCode === AttachmentResultCode.Success;

        if (attachmentFullViewState) {
            stopFakeProgressIndicator(attachmentFullViewState.attachmentId, succeeded);
            saveToCloudPostProcess(
                attachmentFullViewState,
                succeeded,
                succeeded ? payload.Item.Location : null
            );
        }

        if (payload.ResultCode === AttachmentResultCode.Success) {
            setSaveToCloudStatus(SaveToCloudStatus.succeeded, attachmentId);
            setAttachmentOriginalUrl(attachmentId, payload.Item.Location);
            datapoint.end();
        } else {
            setSaveToCloudStatus(SaveToCloudStatus.failed, attachmentId);
            datapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(`ResultCode: ${payload.ResultCode} with exception: ${payload.Exception}`)
            );
        }

        if (isFeatureEnabled('doc-attachment-sigs')) {
            lazyLogSigsDatapoint.importAndExecute('AttachmentSaved', {
                itemId: sxsStore.editState?.itemId?.Id,
                customProperties: { FileId: attachmentId.Id, Destination: 'Cloud' },
            });
        }

        return true;
    };

    const serviceCallFailureCallback = (error: string): void => {
        setSaveToCloudStatus(SaveToCloudStatus.failed, attachmentId);
        datapointCustomData.result = AttachmentResultCode.GenericFailure;
        datapoint.addCustomData(datapointCustomData);
        datapoint.endWithError(DatapointStatus.ServerError, new Error(error));
    };

    setSaveToCloudStatus(SaveToCloudStatus.saving, attachmentId);
    saveAttachmentsToCloud(attachmentId, null, notificationHandler, serviceCallFailureCallback);
});
